import { Button, Stack, Typography } from '@mui/material'
import { forwardRef } from 'react'
import { TextInputField, TextInputFieldProps } from 'utoniq-ui'

import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { Form, useForm, UseFormProps } from '@/core/form'
import { validateEmail } from '@/core/validators'

export type EmailAddressFormSNSProps = {
  state: {
    email: string
  }
  onSubmit: (inputs: EmailAddressFormValues) => Promise<void>
}

export type EmailAddressFormValues = {
  email: string
}

const defaultValues: EmailAddressFormValues = {
  email: '',
}

const schema: UseFormProps['schema'] = o => ({
  email: validateEmail(o, 'emailAddressSettings.email'),
})

/**
 * @description
 * SNSでアカウント作成したユーザー用のフォーム
 * パスワード入力欄がなく、認証コードでメアドを更新する。
 */
export const EmailAddressSettingFormSNS: Organism<EmailAddressFormSNSProps> = ({
  state,
  onSubmit,
}) => {
  const {
    handleSubmit,
    register,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<EmailAddressFormValues>({
    schema,
    mode: 'onChange',
    defaultValues,
  })

  const { showError } = useMessage()

  const handleOnSubmit = handleSubmit(async values => {
    await onSubmit(values).catch(showError)
  })

  return (
    <Form onSubmit={handleOnSubmit}>
      <CurrentEmailTextCell email={state.email} />

      <TextInputCell
        type="email"
        error={errors?.email}
        {...register('email')}
      />
      <Stack spacing={4}>
        <ConfirmTextCell />
        <ButtonCell loading={isSubmitting} />
      </Stack>
    </Form>
  )
}

const CurrentEmailTextCell: Cell<{ email: string }> = ({ email }) => {
  const { t } = useTranslation('emailAddressSettings')

  return (
    <CellFrame>
      <Typography
        variant="subtitle1"
        sx={{
          my: 2,
          color: 'grey.600',
        }}
      >
        {t('currentEmailAddress')}
      </Typography>
      <Typography
        variant="body1"
        sx={{
          mt: 2,
          mb: 7,
          color: 'text.primary',
        }}
      >
        {email}
      </Typography>
    </CellFrame>
  )
}

const TextInputCell = forwardRef(
  (
    props: Omit<TextInputFieldProps, 't'>,
    ref: React.ForwardedRef<HTMLInputElement>
  ) => {
    const { t } = useTranslation('emailAddressSettings')

    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

const ConfirmTextCell = () => {
  const { t } = useTranslation('emailAddressSettings')

  return (
    <CellFrame>
      <Typography variant="body2">{t('confirm')}</Typography>
    </CellFrame>
  )
}

type ButtonCellProps = {
  loading: boolean
}
const ButtonCell: Cell<ButtonCellProps> = ({ loading }) => {
  const { t } = useTranslation('emailAddressSettings')
  const { router } = useRouter()

  return (
    <CellFrame sx={{ px: 4, py: 3, textAlign: 'center' }}>
      <Stack spacing={1} alignItems="center">
        <Button
          type="submit"
          size="large"
          sx={{ width: 244 }}
          loading={loading}
        >
          {t('submit')}
        </Button>
        <Button
          variant="text"
          size="large"
          sx={{ width: 244 }}
          onClick={() => router.goBack()}
        >
          {t('back')}
        </Button>
      </Stack>
    </CellFrame>
  )
}
